import React, { useEffect, lazy, Suspense, useMemo } from "react";
import { Route, Switch, useLocation, Redirect } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getPlatformContentAsync,
  getPublicCollectionsAsync,
  goToInfinitePaginationNextPage
} from "../features/frontend/frontendSlice";
import AuthModal from "../components-common/auth/AuthModal";
import * as Fathom from "fathom-client";
import TopNavbar from "../components-common/pages/layout/TopNavbar";
import ToastNotification from "../components-common/pages/dashboard/general/ToastNotification";
import { LoadingSpinnerCentered } from "../components-common/LoadingSpinner";
import MainPageWithSideBar from "../components-common/pages/layout/MainPageWithSideBar";
import { Body } from "../components-common/pages/layout/Body";
import LeftNavbarWithRouter from "../components-common/pages/layout/LeftNavbar";
import Footer from "../components-common/general/Footer";
import { useWatchCurrentUserForErrorReporting } from "../utils/errorReporting";
import { lazyImportWrapper } from "../utils/lazy-loading";
import { readUserProgressAsync } from "../features/content/contentSlice";
import { getRedirectPath } from "./utils/redirect";

const FrontPage = lazy(() =>
  lazyImportWrapper(() => import(/* webpackChunkName: "FrontPage" */ "./pages/FrontPage"))
);
const GroupPage = lazy(() =>
  lazyImportWrapper(() => import(/* webpackChunkName: "GroupPageV1" */ "./pages/groups/GroupPage"))
);
const ContentPage = lazy(() =>
  lazyImportWrapper(() =>
    import(/* webpackChunkName: "ContentPage" */ "../components-common/pages/ContentPage")
  )
);
const GroupsListPage = lazy(() =>
  lazyImportWrapper(() =>
    import(
      /* webpackChunkName: "GroupsListPage" */ "../components-common/pages/groups/GroupsListPage"
    )
  )
);
const AuthPage = lazy(() =>
  lazyImportWrapper(() =>
    import(/* webpackChunkName: "AuthPage" */ "../components-common/pages/AuthPage")
  )
);
const AuthMagicGroup = lazy(() =>
  lazyImportWrapper(() =>
    import(/* webpackChunkName: "AuthMagicGroup" */ "../components-common/pages/AuthMagicGroup")
  )
);
const EmbedPage = lazy(() =>
  lazyImportWrapper(() =>
    import(/* webpackChunkName: "EmbedPage" */ "../components-common/pages/EmbedPage")
  )
);
const CreatorsPage = lazy(() =>
  lazyImportWrapper(() =>
    import(/* webpackChunkName: "CreatorsPage" */ "../components-common/pages/CreatorsPage")
  )
);
const ResetPasswordPage = lazy(() =>
  lazyImportWrapper(() =>
    import(
      /* webpackChunkName: "PasswordResetPage" */ "../components-common/pages/ResetPasswordPage"
    )
  )
);
const TagsSearchResultsPage = lazy(() =>
  lazyImportWrapper(() =>
    import(
      /* webpackChunkName: "TagsSearchResultsPage" */ "../components-common/pages/TagsSearchResultsPage"
    )
  )
);
const CollectionPage = lazy(() =>
  lazyImportWrapper(() =>
    import(/* webpackChunkName: "CollectionPage" */ "../components-common/pages/CollectionPage")
  )
);
const SearchResultsPage = lazy(() =>
  lazyImportWrapper(() =>
    import(
      /* webpackChunkName: "SearchResultsPage" */ "../components-common/pages/SearchResultsPage"
    )
  )
);
const TopicPage = lazy(() =>
  lazyImportWrapper(() =>
    import(/* webpackChunkName: "TopicPage" */ "../components-common/pages/TopicPage")
  )
);
const ContentChatOnly = lazy(() =>
  lazyImportWrapper(() =>
    import(/* webpackChunkName: "ContentChatOnly" */ "../components-common/pages/ContentChatOnly")
  )
);
const ProfilePage = lazy(() =>
  lazyImportWrapper(() =>
    import(/* webpackChunkName: "ProfilePage" */ "../components-common/pages/ProfilePage")
  )
);
const DashboardLayout = lazy(() =>
  lazyImportWrapper(() =>
    import(
      /* webpackChunkName: "DashboardLayout" */ "../components-common/pages/dashboard/general/DashboardLayout"
    )
  )
);
const ErrorPage404 = lazy(() =>
  lazyImportWrapper(() =>
    import(/* webpackChunkName: "ErrorPage404" */ "../components-common/pages/404/ErrorPage404")
  )
);
const GroupAboutPage = lazy(() =>
  lazyImportWrapper(() =>
    import(
      /* webpackChunkName: "GroupAboutPage" */ "../components-common/pages/groups/GroupAboutPage"
    )
  )
);
const LeadPage = lazy(() =>
  lazyImportWrapper(() =>
    import(/* webpackChunkName: "LeadPage" */ "../components-v2/pages/LeadPage")
  )
);
const DiscoverPage = lazy(() =>
  lazyImportWrapper(() =>
    import(/* webpackChunkName: "DiscoverPage" */ "../components-v2/pages/DiscoverPage")
  )
);
const SharePage = lazy(() =>
  lazyImportWrapper(() =>
    import(/* webpackChunkName: "SharePage" */ "../components-common/pages/SharePage")
  )
);
const ChatEmbedPage = lazy(() =>
  lazyImportWrapper(() =>
    import(/* webpackChunkName: "ChatEmbedPage" */ "../components-common/pages/ChatEmbedPage")
  )
);

const AppV1 = () => {
  console.log("AppV1");

  const location = useLocation();
  const dispatch = useDispatch();

  const { platformData, loading } = useSelector(state => state.frontend);
  const { currentUser } = useSelector(state => state.auth);

  useWatchCurrentUserForErrorReporting();

  const redirectPath = useMemo(() => {
    if (loading) {
      return null;
    }

    return getRedirectPath(
      location.pathname,
      platformData.dashboardOnly,
      platformData.firebaseCredentials
    );
  }, [location.pathname, loading, platformData.dashboardOnly, platformData.firebaseCredentials]);

  useEffect(() => {
    if (platformData.fathomAnalytics && !location.pathname.includes("dashboard")) {
      Fathom.load(platformData.fathomAnalytics);
    }
  }, [platformData.fathomAnalytics, location.pathname]);

  useEffect(() => {
    const isOnEmbedPage = location.pathname.includes("embed");
    const wasCustomCodeAlreadyInitialised = document.getElementById("custom-css");
    if (platformData.id && !wasCustomCodeAlreadyInitialised && !isOnEmbedPage) {
      if (platformData.customCSS) {
        const style = document.createElement("style");
        style.id = "custom-css";
        style.textContent = platformData.customCSS;
        document.head.appendChild(style);
      }

      if (platformData.customJS) {
        const fragment = document.createRange().createContextualFragment(platformData.customJS);
        document.body.appendChild(fragment);
      }
    }
  }, [dispatch, platformData, location.pathname]);

  useEffect(() => {
    if (!platformData || platformData.loading || platformData.error || !platformData.id) {
      return;
    }

    dispatch(getPlatformContentAsync({ platformData }));
    dispatch(goToInfinitePaginationNextPage());
    dispatch(getPublicCollectionsAsync({ platformData, itemsPerPage: 4 }));
  }, [dispatch, platformData]);

  useEffect(() => {
    if (currentUser) dispatch(readUserProgressAsync({ currentUser }));
  }, [dispatch, currentUser]);

  if (redirectPath) {
    return <Redirect to={redirectPath} />;
  }

  return (
    <Body>
      <Suspense fallback={<LoadingSpinnerCentered />}>
        <Switch location={location}>
          <Route exact path="/lead/:content_id" component={LeadPage} />
          <Route exact path="/share/:id" component={SharePage} />
          <Route exact path="/group/about/:id" component={GroupAboutPage} />
          <Route exact path="/discover" component={DiscoverPage} />
          <Route exact path="/chatembed/:group_id" component={ChatEmbedPage} />
          <Route>
            <MainPageWithSideBar>
              <TopNavbar />
              <LeftNavbarWithRouter />
              <ToastNotification />
              <Switch location={location}>
                <Route exact path="/" children={<FrontPage />} />
                <Route exact path="/groups/:slug" component={GroupPage} />

                <Route
                  exact
                  path="/groups/:slug/:collection_slug/:content_id"
                  component={ContentPage}
                />
                <Route exact path="/groups" component={GroupsListPage} />
                <Route path="/creators" children={<CreatorsPage />} />
                <Route exact path="/reset-password" children={<ResetPasswordPage />} />
                <Route path="/search" children={<SearchResultsPage />} />
                <Route path="/tags/:tag" children={<TagsSearchResultsPage />} />
                <Route exact path="/auth-group/:form/:groupID?" component={AuthMagicGroup} />
                <Route exact path="/auth/:form/:role?" component={AuthPage} />
                <Route path="/dashboard" children={<DashboardLayout />} />
                <Route exact path="/viewcontent/:content_id" component={ContentPage} />
                <Route exact path="/topic/:topic_slug" children={<TopicPage />} />
                <Route exact path="/profile/:userId" component={ProfilePage} />
                <Route exact path="/embed/:content_id" component={EmbedPage} />

                <Route exact path="/:collection_slug" children={<CollectionPage />} />
                <Route exact path="/:collection_slug/:content_id" component={ContentPage} />
                <Route
                  exact
                  path="/:collection_slug/:content_id/chat"
                  children={<ContentChatOnly />}
                />

                <Route path="*">
                  <ErrorPage404 />
                </Route>
              </Switch>
            </MainPageWithSideBar>
          </Route>
        </Switch>
      </Suspense>
      <AuthModal />
      <Footer />
    </Body>
  );
};

export default AppV1;
